import type { ProductDTO } from "@/modules/products/domain/contracts";
import { InfiniteCheckboxList } from "@/shared/ui/InfiniteCheckboxList";
import { productsApi } from "../api/client";
import { productKeys } from "../api/queries";

const LIMIT = 20;

function getProductLabel(p: ProductDTO): string {
  const name = p.name as { lo?: string; th?: string; en?: string } | undefined;
  if (!name) return p.slug ?? p.id;
  return name.lo ?? name.th ?? name.en ?? p.slug ?? p.id;
}

function getProductImageSrc(p: ProductDTO): string | null {
  const first = p.images?.[0];
  return first?.fileKey ?? null;
}

function productsQueryFn(params: {
  search: string;
  pageParam: number;
}): Promise<{ items: ProductDTO[]; nextPage: number | null }> {
  const offset = (params.pageParam - 1) * LIMIT;
  return productsApi
    .list({
      search: params.search || undefined,
      limit: LIMIT,
      offset,
      status: "active",
      isSellable: true,
    })
    .then((res) => {
      const { data, meta } = res;
      const hasMore = meta.offset + meta.limit < meta.total;
      return {
        items: data,
        nextPage: hasMore ? params.pageParam + 1 : null,
      };
    });
}

async function preloadProducts(ids: string[]): Promise<ProductDTO[]> {
  if (ids.length === 0) return [];
  const results = await Promise.all(
    ids.map((id) => productsApi.get(id).catch(() => null as ProductDTO | null)),
  );
  return results.filter((p): p is ProductDTO => p != null);
}

export interface ProductCheckboxPickerProps {
  value: string[];
  onChange: (ids: string[]) => void;
  searchPlaceholder?: string;
  listHeight?: number;
  className?: string;
}

export function ProductCheckboxPicker({
  value,
  onChange,
  searchPlaceholder = "ຄົ້ນຫາຕາມຊື່ສິນຄ້າ...",
  listHeight,
  className,
}: ProductCheckboxPickerProps) {
  return (
    <InfiniteCheckboxList<ProductDTO>
      queryKey={productKeys.list({})}
      queryFn={productsQueryFn}
      preloadQueryFn={preloadProducts}
      getLabel={getProductLabel}
      getValue={(p) => p.id}
      getImageSrc={getProductImageSrc}
      values={value}
      onValuesChange={onChange}
      searchPlaceholder={searchPlaceholder}
      emptyMessage="ບໍ່ພົບສິນຄ້າ"
      listHeight={listHeight}
      className={className}
    />
  );
}
